import { useState, useMemo, useEffect } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import AuthPage from "./AuthPage";
import ScorePage from "./ScorePage";
import Dashboard from "./Dashboard";
import TournamentEntryPage from "./TournamentEntryPage";
import AdminPage from "./AdminPage";
import { computePlayerAverages } from "./lib/playerAverages";
import { ResultsProvider, useResults } from "./lib/ResultsContext";

// Stripe redirects back with ?page=enter&entryId=...&payment=success|cancelled —
// read that once on load so a full-page navigation lands on the right screen.
function getInitialRoute() {
  var params = new URLSearchParams(window.location.search);
  return {
    page: params.get("page") || "home",
    entryId: params.get("entryId") || null,
    payment: params.get("payment") || null,
  };
}

// ── Nav ───────────────────────────────────────────────────────────────────────

function NavBar({ activePage, onNavigate, authUser, onAuthNav }) {
  var btn = function(page, label) {
    var active = activePage === page;
    return (
      <button onClick={function() { onNavigate(page); }} style={{ background: "none", border: "none", borderBottom: active ? "2px solid #FFB020" : "2px solid transparent", color: active ? "#FFB020" : "#666", fontSize: 15, letterSpacing: 1, textTransform: "uppercase", padding: "12px 20px", cursor: "pointer" }}>
        {label}
      </button>
    );
  };
  return (
    <div style={{
  background: "rgba(0,0,0,0.6)",
  borderBottom: "1px solid rgba(255,165,0,0.1)",
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "center",
  gap: 4,
  position: "sticky",
  top: 0,
  zIndex: 100,
  paddingTop: "env(safe-area-inset-top)"
}}>
      {btn("home", "🏠 Home")}
      {btn("matches", "🎯 Results")}
      {btn("rankings", "🏆 Rankings")}
      {btn("averages", "📊 Averages")}
      {btn("tournaments", "🎯 Tournaments")}
      {btn("enter", "🎟 Enter")}
      {authUser && btn("scorer", "🎲 Scorer")}
      {authUser && authUser.isAdmin && btn("admin", "🛠 Admin")}
      <button onClick={onAuthNav} style={{ background: authUser ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.06)", border: authUser ? "1px solid rgba(255,176,32,0.4)" : "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: authUser ? "#FFB020" : "#888", fontSize: 13, padding: "6px 14px", cursor: "pointer", margin: "6px 8px", fontFamily: "sans-serif" }}>
        {authUser ? ("👤 " + (authUser.playerName ? authUser.playerName.split(",")[0].trim() : "My Account")) : "Log In"}
      </button>
    </div>
  );
}

// ── Home ─────────────────────────────────────────────────────────────────────

function HomePage({ onNavigate }) {
  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "40px 20px", textAlign: "center", color: "#fff", fontFamily: "sans-serif" }}>
      <div style={{ marginBottom: 32 }}>
        <img
          src="edc-logo.png"
          alt="Eastern Darts Corporation"
          style={{ width: "100%", borderRadius: 16, display: "block" }}
          onError={function(e) { e.target.style.display = "none"; }}
        />
      </div>
      <h1 style={{ fontSize: 42, fontWeight: 800, textTransform: "uppercase", letterSpacing: 2, margin: "16px 0 12px", background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
        Eastern Darts Corporation
      </h1>
      <p style={{ color: "#666", fontSize: 15, marginBottom: 40 }}>
        Results, rankings and averages for EDC events and open tournaments
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
        {[
          { page: "matches", icon: "🎯", label: "Results", desc: "Match results from all tournaments" },
          { page: "rankings", icon: "🏆", label: "Rankings", desc: "12-month rolling rankings" },
          { page: "averages", icon: "📊", label: "Averages", desc: "Player averages leaderboard" },
          { page: "tournaments", icon: "🗓", label: "Tournaments", desc: "Browse draws and brackets" },
        ].map(function(item, i) {
          return (
            <div key={i} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,165,0,0.15)", borderRadius: 14, padding: "24px 16px", cursor: "pointer", transition: "border-color 0.2s" }}
              onClick={function() { onNavigate(item.page); }}
              onMouseEnter={function(e) { e.currentTarget.style.borderColor = "rgba(255,165,0,0.5)"; }}
              onMouseLeave={function(e) { e.currentTarget.style.borderColor = "rgba(255,165,0,0.15)"; }}
            >
              <div style={{ fontSize: 36, marginBottom: 10 }}>{item.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#FFB020", marginBottom: 6 }}>{item.label}</div>
              <div style={{ fontSize: 13, color: "#666" }}>{item.desc}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Results ───────────────────────────────────────────────────────────────────

function MatchCard({ match }) {
  var p1Won = match.score1 > match.score2;
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,165,0,0.15)", borderRadius: 12, padding: "16px 20px", display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, alignItems: "center" }}>
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: 18, fontWeight: p1Won ? 700 : 400, color: p1Won ? "#FFB020" : "#ccc" }}>{match.player1}{p1Won ? " 🏆" : ""}</div>
        {p1Won && match.winner_avg ? <div style={{ fontSize: 11, color: "#888" }}>{"avg " + match.winner_avg}</div> : null}
        {!p1Won && match.loser_avg ? <div style={{ fontSize: 11, color: "#888" }}>{"avg " + match.loser_avg}</div> : null}
      </div>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{match.score1 + " - " + match.score2}</div>
        <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>{match.stage}</div>
      </div>
      <div style={{ textAlign: "left" }}>
        <div style={{ fontSize: 18, fontWeight: !p1Won ? 700 : 400, color: !p1Won ? "#FFB020" : "#ccc" }}>{(!p1Won ? "🏆 " : "") + match.player2}</div>
        {!p1Won && match.winner_avg ? <div style={{ fontSize: 11, color: "#888" }}>{"avg " + match.winner_avg}</div> : null}
        {p1Won && match.loser_avg ? <div style={{ fontSize: 11, color: "#888" }}>{"avg " + match.loser_avg}</div> : null}
      </div>
      <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 8 }}>
        <span style={{ fontSize: 12, color: "#666" }}>{new Date(match.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
        <span style={{ fontSize: 11, color: "#FFB020", background: "rgba(255,176,32,0.1)", padding: "2px 10px", borderRadius: 20, border: "1px solid rgba(255,176,32,0.2)" }}>{match.tournament}</span>
      </div>
    </div>
  );
}

function MatchList() {
  var allMatches = useResults().matches;
  var [playerQ, setPlayerQ] = useState("");
  var [playerQ2, setPlayerQ2] = useState("");
  var [tournQ, setTournQ] = useState("");
  var [stageQ, setStageQ] = useState("");
  var [sortQ, setSortQ] = useState("date_desc");
var [showP1, setShowP1] = useState(false);
var [showP2, setShowP2] = useState(false);

  var allTournaments = useMemo(function() {
    return [...new Set(allMatches.map(function(m) { return m.tournament; }).filter(Boolean))].sort();
  }, [allMatches]);
  var allStages = useMemo(function() {
    return [...new Set(allMatches.map(function(m) { return m.stage; }).filter(Boolean))].sort();
  }, [allMatches]);
  var allPlayers = useMemo(function() {
    var seen = {};
    allMatches.forEach(function(m) { seen[m.player1] = 1; seen[m.player2] = 1; });
    return Object.keys(seen).sort();
  }, [allMatches]);

  var shown = useMemo(function() {
    var arr = allMatches.slice();
    if (playerQ && playerQ2) {
      // H2H mode: only matches where both players appear
      var q1 = playerQ.toLowerCase(); var q2 = playerQ2.toLowerCase();
      arr = arr.filter(function(m) {
        var has1 = m.player1.toLowerCase().indexOf(q1) !== -1 || m.player2.toLowerCase().indexOf(q1) !== -1;
        var has2 = m.player1.toLowerCase().indexOf(q2) !== -1 || m.player2.toLowerCase().indexOf(q2) !== -1;
        return has1 && has2;
      });
    } else if (playerQ) {
      arr = arr.filter(function(m) { return m.player1.toLowerCase().indexOf(playerQ.toLowerCase()) !== -1 || m.player2.toLowerCase().indexOf(playerQ.toLowerCase()) !== -1; });
    } else if (playerQ2) {
      arr = arr.filter(function(m) { return m.player1.toLowerCase().indexOf(playerQ2.toLowerCase()) !== -1 || m.player2.toLowerCase().indexOf(playerQ2.toLowerCase()) !== -1; });
    }
    if (tournQ) arr = arr.filter(function(m) { return m.tournament === tournQ; });
    if (stageQ) arr = arr.filter(function(m) { return m.stage === stageQ; });
    arr.sort(function(a, b) {
      if (sortQ === "date_desc") return new Date(b.date) - new Date(a.date);
      if (sortQ === "date_asc") return new Date(a.date) - new Date(b.date);
      return a.tournament.localeCompare(b.tournament);
    });
    return arr;
  }, [allMatches, playerQ, playerQ2, tournQ, stageQ, sortQ]);

  // Player summary — only when a single player is searched
  var playerSummary = useMemo(function() {
    var q = playerQ.trim().toLowerCase();
    if (!q) return null;
    // Find exact or close match
    var playerMatches = allMatches.filter(function(m) {
      return m.player1.toLowerCase().indexOf(q) !== -1 || m.player2.toLowerCase().indexOf(q) !== -1;
    });
    if (playerMatches.length === 0) return null;
    // Identify the player name (pick most common matching name)
    var nameCounts = {};
    playerMatches.forEach(function(m) {
      [m.player1, m.player2].forEach(function(name) {
        if (name.toLowerCase().indexOf(q) !== -1) nameCounts[name] = (nameCounts[name] || 0) + 1;
      });
    });
    var playerName = Object.keys(nameCounts).sort(function(a, b) { return nameCounts[b] - nameCounts[a]; })[0];
    // Stats for that player
    var wins = 0, losses = 0, winAvgs = [], loseAvgs = [];
    playerMatches.forEach(function(m) {
      var isP1 = m.player1 === playerName;
      var isP2 = m.player2 === playerName;
      if (!isP1 && !isP2) return;
      var won = (isP1 && m.score1 > m.score2) || (isP2 && m.score2 > m.score1);
      if (won) {
        wins++;
        if (m.winner_avg) winAvgs.push(parseFloat(m.winner_avg));
      } else {
        losses++;
        if (m.loser_avg) loseAvgs.push(parseFloat(m.loser_avg));
      }
    });
    var total = wins + losses;
    var allAvgs = winAvgs.concat(loseAvgs);
    var avgScore = allAvgs.length > 0 ? (allAvgs.reduce(function(a, b) { return a + b; }, 0) / allAvgs.length) : null;
    var bestAvg = allAvgs.length > 0 ? Math.max.apply(null, allAvgs) : null;
    var winPct = total > 0 ? Math.round((wins / total) * 100) : 0;
    return { name: playerName, wins: wins, losses: losses, total: total, winPct: winPct, avgScore: avgScore ? avgScore.toFixed(2) : null, bestAvg: bestAvg ? bestAvg.toFixed(2) : null };
  }, [allMatches, playerQ]);

  // H2H summary
  var h2hSummary = useMemo(function() {
    var q1 = playerQ.trim().toLowerCase(); var q2 = playerQ2.trim().toLowerCase();
    if (!q1 || !q2) return null;
    var h2hMatches = allMatches.filter(function(m) {
      var has1 = m.player1.toLowerCase().indexOf(q1) !== -1 || m.player2.toLowerCase().indexOf(q1) !== -1;
      var has2 = m.player1.toLowerCase().indexOf(q2) !== -1 || m.player2.toLowerCase().indexOf(q2) !== -1;
      return has1 && has2;
    });
    if (h2hMatches.length === 0) return null;
    // Resolve names
    var n1counts = {}, n2counts = {};
    h2hMatches.forEach(function(m) {
      [m.player1, m.player2].forEach(function(name) {
        if (name.toLowerCase().indexOf(q1) !== -1) n1counts[name] = (n1counts[name] || 0) + 1;
        if (name.toLowerCase().indexOf(q2) !== -1) n2counts[name] = (n2counts[name] || 0) + 1;
      });
    });
    var name1 = Object.keys(n1counts).sort(function(a,b){ return n1counts[b]-n1counts[a]; })[0];
    var name2 = Object.keys(n2counts).sort(function(a,b){ return n2counts[b]-n2counts[a]; })[0];
    if (!name1 || !name2 || name1 === name2) return null;
    var w1=0, w2=0, avgs1=[], avgs2=[];
    h2hMatches.forEach(function(m) {
      var p1is1 = m.player1 === name1;
      var won1 = (p1is1 && m.score1 > m.score2) || (!p1is1 && m.score2 > m.score1);
      if (won1) {
        w1++;
        if (m.winner_avg) avgs1.push(parseFloat(m.winner_avg));
        if (m.loser_avg) avgs2.push(parseFloat(m.loser_avg));
      } else {
        w2++;
        if (m.winner_avg) avgs2.push(parseFloat(m.winner_avg));
        if (m.loser_avg) avgs1.push(parseFloat(m.loser_avg));
      }
    });
    var avg1 = avgs1.length > 0 ? (avgs1.reduce(function(a,b){return a+b;},0)/avgs1.length).toFixed(2) : null;
    var avg2 = avgs2.length > 0 ? (avgs2.reduce(function(a,b){return a+b;},0)/avgs2.length).toFixed(2) : null;
    return { name1: name1, name2: name2, w1: w1, w2: w2, total: h2hMatches.length, avg1: avg1, avg2: avg2 };
  }, [allMatches, playerQ, playerQ2]);

  var sel = { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "9px 12px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "32px 20px" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 36 }}>{"🎯"}</div>
        <h1 style={{ margin: "8px 0 4px", fontSize: 40, fontWeight: 800, textTransform: "uppercase", background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Darts Results</h1>
        <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{allMatches.length + " results across " + allTournaments.length + " tournaments"}</p>
      </div>
     <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,165,0,0.1)", borderRadius: 14, padding: 20, marginBottom: 20 }}>
  <div>
    <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Player 1</div>
    <div style={{ position: "relative" }}>
      <input
        value={playerQ}
        onChange={function(e) { setPlayerQ(e.target.value); setShowP1(true); }}
        onFocus={function() { setShowP1(true); }}
        onBlur={function() { setTimeout(function() { setShowP1(false); }, 150); }}
        placeholder="Search..."
        style={sel}
      />
      {showP1 && playerQ && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 6, maxHeight: 180, overflowY: "auto", zIndex: 200
        }}>
          {allPlayers
            .filter(function(p) { return p.toLowerCase().indexOf(playerQ.toLowerCase()) !== -1; })
            .slice(0, 8)
            .map(function(p) {
              return (
                <div key={p} onClick={function() { setPlayerQ(p); setShowP1(false); }}
                  style={{ padding: "8px 12px", cursor: "pointer", color: "#ccc", fontSize: 14 }}>
                  {p}
                </div>
              );
            })}
        </div>
      )}
    </div>
  </div>
  <div>
    <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Player 2</div>
    <div style={{ position: "relative" }}>
      <input
        value={playerQ2}
        onChange={function(e) { setPlayerQ2(e.target.value); setShowP2(true); }}
        onFocus={function() { setShowP2(true); }}
        onBlur={function() { setTimeout(function() { setShowP2(false); }, 150); }}
        placeholder="Compare..."
        style={sel}
      />
      {showP2 && playerQ2 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0,
          background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 6, maxHeight: 180, overflowY: "auto", zIndex: 200
        }}>
          {allPlayers
            .filter(function(p) { return p.toLowerCase().indexOf(playerQ2.toLowerCase()) !== -1; })
            .slice(0, 8)
            .map(function(p) {
              return (
                <div key={p} onClick={function() { setPlayerQ2(p); setShowP2(false); }}
                  style={{ padding: "8px 12px", cursor: "pointer", color: "#ccc", fontSize: 14 }}>
                  {p}
                </div>
              );
            })}
        </div>
      )}
    </div>
  </div>
  <div>
    <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Tournament</div>
    <select value={tournQ} onChange={function(e) { setTournQ(e.target.value); }} style={sel}>
      <option value="">All</option>
      {allTournaments.map(function(t) { return <option key={t} value={t}>{t}</option>; })}
    </select>
  </div>
  <div>
    <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Stage</div>
    <select value={stageQ} onChange={function(e) { setStageQ(e.target.value); }} style={sel}>
      <option value="">All</option>
      {allStages.map(function(s) { return <option key={s} value={s}>{s}</option>; })}
    </select>
  </div>
  <div>
    <div style={{ fontSize: 11, color: "#888", marginBottom: 5, textTransform: "uppercase", letterSpacing: 1 }}>Sort</div>
    <select value={sortQ} onChange={function(e) { setSortQ(e.target.value); }} style={sel}>
      <option value="date_desc">Newest first</option>
      <option value="date_asc">Oldest first</option>
      <option value="tournament">Tournament A-Z</option>
    </select>
  </div>
</div>
      {h2hSummary ? (
        <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 14, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ textAlign: "center", fontSize: 12, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 16 }}>Head to Head · {h2hSummary.total + " match" + (h2hSummary.total === 1 ? "" : "es")}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "center" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: h2hSummary.w1 > h2hSummary.w2 ? "#FFB020" : "#ddd", marginBottom: 8 }}>{h2hSummary.name1}</div>
              <div style={{ fontSize: 44, fontWeight: 800, color: h2hSummary.w1 > h2hSummary.w2 ? "#FFB020" : "#fff", lineHeight: 1 }}>{h2hSummary.w1}</div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>wins</div>
              {h2hSummary.avg1 && <div style={{ marginTop: 10, fontSize: 13, color: "#7BA7FF" }}>{"avg " + h2hSummary.avg1}</div>}
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#444" }}>vs</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: h2hSummary.w2 > h2hSummary.w1 ? "#FFB020" : "#ddd", marginBottom: 8 }}>{h2hSummary.name2}</div>
              <div style={{ fontSize: 44, fontWeight: 800, color: h2hSummary.w2 > h2hSummary.w1 ? "#FFB020" : "#fff", lineHeight: 1 }}>{h2hSummary.w2}</div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 4, textTransform: "uppercase", letterSpacing: 1 }}>wins</div>
              {h2hSummary.avg2 && <div style={{ marginTop: 10, fontSize: 13, color: "#7BA7FF" }}>{"avg " + h2hSummary.avg2}</div>}
            </div>
          </div>
          <div style={{ marginTop: 16, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: (h2hSummary.total > 0 ? Math.round((h2hSummary.w1 / h2hSummary.total) * 100) : 50) + "%", background: "linear-gradient(90deg,#FFB020,#FF6B00)", borderRadius: 3, transition: "width 0.4s" }} />
          </div>
        </div>
      ) : playerSummary ? (
        <div style={{ background: "rgba(255,176,32,0.06)", border: "1px solid rgba(255,176,32,0.2)", borderRadius: 14, padding: "18px 24px", marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#FFB020", marginBottom: 14 }}>{playerSummary.name}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#fff" }}>{playerSummary.total}</div>
              <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Played</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#6DC878" }}>{playerSummary.wins}</div>
              <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Wins</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#aaa" }}>{playerSummary.losses}</div>
              <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Losses</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#FFB020" }}>{playerSummary.winPct + "%"}</div>
              <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Win %</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: "#7BA7FF" }}>{playerSummary.avgScore ? playerSummary.avgScore : "-"}</div>
              <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>Avg</div>
            </div>
          </div>
          {playerSummary.bestAvg && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 12, color: "#666" }}>
              {"Best avg: "}<span style={{ color: "#7BA7FF", fontWeight: 600 }}>{playerSummary.bestAvg}</span>
            </div>
          )}
        </div>
      ) : null}
      <div style={{ fontSize: 12, color: "#555", marginBottom: 12 }}>{"Showing " + shown.length + " of " + allMatches.length + " results"}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {shown.length === 0
          ? <div style={{ textAlign: "center", color: "#555", padding: "60px 0" }}>No results found</div>
          : shown.map(function(m) { return <MatchCard key={m.id} match={m} />; })
        }
      </div>
    </div>
  );
}

// ── Rankings ──────────────────────────────────────────────────────────────────

function RankingsPage(props) {
  var onNavigateToTournament = props.onNavigateToTournament;
  var [search, setSearch] = useState(props.initialSearch || "");
  var [openIdx, setOpenIdx] = useState(null);
  var [gradeFilter, setGradeFilter] = useState("all");
  var [asOf, setAsOf] = useState("");
  var results = useResults();
  var viewingHistory = !!asOf && asOf < results.rankingsDate;
  var allRankings = results.rankingsAsOf(viewingHistory ? asOf : "");

  var shown = allRankings.filter(function(p) {
    var nameMatch = p.player.toLowerCase().indexOf(search.toLowerCase()) !== -1;
    var gradeMatch = gradeFilter === "all" || p.results.some(function(r) { return r.grade === gradeFilter; });
    return nameMatch && gradeMatch;
  });

  var maxPts = allRankings[0] ? allRankings[0].total_points : 1;
  var gradeColour = { "Grade A": "#FFB020", "Grade B": "#7BA7FF", "Grade C": "#6DC878" };
  var grades = ["all", "Grade A", "Grade B", "Grade C"];

  return (
    <div style={{ padding: "24px 20px", maxWidth: 780, margin: "0 auto", color: "#fff", fontFamily: "sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 40 }}>{"🏆"}</div>
        <h1 style={{ margin: "8px 0 4px", fontSize: 40, fontWeight: 800, textTransform: "uppercase", background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Rankings</h1>
        <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{"Rolling 12-month · " + allRankings.length + " players · EDC Thursday events excluded"}</p>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1 }}>Rankings as of</span>
        <input
          type="date"
          value={viewingHistory ? asOf : results.rankingsDate}
          min={results.earliestDate || undefined}
          max={results.rankingsDate}
          onChange={function(e) { setAsOf(e.target.value); setOpenIdx(null); }}
          style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "7px 10px", fontSize: 13, outline: "none", colorScheme: "dark" }}
        />
        {viewingHistory && (
          <button onClick={function() { setAsOf(""); setOpenIdx(null); }} style={{ background: "rgba(255,176,32,0.15)", border: "1px solid rgba(255,176,32,0.4)", borderRadius: 8, color: "#FFB020", padding: "7px 12px", fontSize: 12, cursor: "pointer" }}>Back to today</button>
        )}
      </div>
      {viewingHistory && (
        <div style={{ background: "rgba(123,167,255,0.08)", border: "1px solid rgba(123,167,255,0.3)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#7BA7FF", marginBottom: 16, textAlign: "center" }}>
          {"Historical rankings — how they stood on " + new Date(asOf + "T00:00:00Z").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) + " (the 12 months up to that date)."}
        </div>
      )}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={search} onChange={function(e) { setSearch(e.target.value); setOpenIdx(null); }} placeholder="Search player..." style={{ flex: 1, minWidth: 160, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "9px 14px", fontSize: 14, outline: "none" }} />
        <div style={{ display: "flex", gap: 6 }}>
          {grades.map(function(g, gi) {
            var active = gradeFilter === g;
            return (
              <button key={gi} onClick={function() { setGradeFilter(g); }} style={{ background: active ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.05)", border: active ? "1px solid rgba(255,176,32,0.4)" : "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: active ? "#FFB020" : "#777", padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>
                {g === "all" ? "All" : g}
              </button>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>{"Showing " + shown.length + " of " + allRankings.length + " players"}</div>
      {shown.length === 0 && (
        <div style={{ textAlign: "center", color: "#555", padding: "40px 0", fontSize: 14 }}>No ranked players for this selection.</div>
      )}
      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,165,0,0.1)", borderRadius: 14, overflow: "hidden" }}>
        {shown.map(function(p, pi) {
          var rank = allRankings.indexOf(p) + 1;
          var pct = Math.round((p.total_points / maxPts) * 100);
          var isOpen = openIdx === pi;
          var medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
          var titles = p.results.filter(function(r) { return r.stage === "Final" && r.draw === "Main" && r.outcome === "W"; }).length;
          var tournsPlayed = {};
          p.results.forEach(function(r) { tournsPlayed[r.tournament + "__" + r.grade] = 1; });
          var tournCount = Object.keys(tournsPlayed).length;
          // Previous rank movement
          var prevRank = p.prev_rank || null;
          var prevPts = p.prev_points || null;
          var rankDiff = prevRank ? prevRank - rank : null; // positive = moved up
          var ptsDiff = prevPts !== null ? p.total_points - prevPts : null;
          var isNew = prevRank === null;
          var moveColour = rankDiff > 0 ? "#6DC878" : rankDiff < 0 ? "#FF6B6B" : "#666";
          var moveText = isNew ? "NEW" : rankDiff === 0 ? "—" : (rankDiff > 0 ? "+" : "") + rankDiff;
          var grouped = {};
          p.results.forEach(function(r) {
            var key = r.tournament + "__" + r.grade;
            if (!grouped[key]) grouped[key] = { tournament: r.tournament, grade: r.grade, date: r.date, pts: 0, rows: [] };
            grouped[key].pts += r.points;
            grouped[key].rows.push(r);
          });
          var groups = Object.values(grouped).sort(function(a, b) { return b.date.localeCompare(a.date); });
          var prevRankLabel = isNew ? "New entry" : ("Was #" + prevRank + " · " + (ptsDiff > 0 ? "+" : "") + ptsDiff + " pts");
          return (
            <div key={pi} style={{ borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
              <div onClick={function() { setOpenIdx(isOpen ? null : pi); }} style={{ display: "grid", gridTemplateColumns: "44px 1fr 90px 20px", alignItems: "center", padding: "12px 16px", cursor: "pointer", background: isOpen ? "rgba(255,176,32,0.04)" : "transparent" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: medal ? 20 : 14, fontWeight: 700, color: "#555" }}>{medal ? medal : rank}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: moveColour, marginTop: 2 }}>{moveText}</div>
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: rank <= 3 ? "#FFB020" : "#ddd" }}>{p.player}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3 }}>
                    <span style={{ fontSize: 11, color: "#555" }}>{tournCount + " tourn" + (tournCount === 1 ? "" : "s")}</span>
                    {titles > 0 && <span style={{ fontSize: 11, color: "#FFB020" }}>{"🏆 " + titles + " title" + (titles === 1 ? "" : "s")}</span>}
                  </div>
                  <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: pct + "%", background: "linear-gradient(90deg,#FFB020,#FF6B00)", borderRadius: 2 }} />
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: rank <= 3 ? "#FFB020" : "#fff" }}>
                    {p.total_points}<span style={{ fontSize: 10, color: "#666", fontWeight: 400, marginLeft: 3 }}>pts</span>
                  </div>
                  {ptsDiff !== null && ptsDiff !== 0 && (
                    <div style={{ fontSize: 10, color: ptsDiff > 0 ? "#6DC878" : "#FF6B6B", marginTop: 2 }}>
                      {(ptsDiff > 0 ? "+" : "") + ptsDiff + " pts"}
                    </div>
                  )}
                  {isNew && <div style={{ fontSize: 10, color: "#FFB020", marginTop: 2 }}>new entry</div>}
                </div>
                <div style={{ textAlign: "right", fontSize: 10, color: "#555", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>{"▼"}</div>
              </div>
              {isOpen && (
                <div style={{ padding: "4px 16px 14px 60px" }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 8, paddingBottom: 6, borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                    {prevRankLabel}
                    {prevPts !== null && <span style={{ marginLeft: 8, color: "#444" }}>{"prev " + prevPts + " pts"}</span>}
                  </div>
                  {groups.map(function(grp, gi) {
                    var gc = gradeColour[grp.grade] || "#aaa";
                    return (
                      <div key={gi} style={{ marginBottom: 6, background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 12px", border: "0.5px solid rgba(255,255,255,0.06)" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span
                              onClick={function(e) { e.stopPropagation(); onNavigateToTournament(grp.tournament, grp.grade); }}
                              style={{ fontSize: 13, color: "#7BA7FF", cursor: "pointer", textDecoration: "underline" }}
                            >
                              {grp.tournament}
                            </span>
                            <span style={{ fontSize: 10, color: gc, border: "1px solid " + gc + "44", borderRadius: 20, padding: "1px 7px" }}>{grp.grade}</span>
                            <span style={{ fontSize: 10, color: "#555" }}>{new Date(grp.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: "#FFB020" }}>{"+" + grp.pts}</span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {grp.rows.map(function(row, ri) {
                            return (
                              <span key={ri} style={{ fontSize: 11, color: "#888", background: "rgba(255,255,255,0.04)", borderRadius: 4, padding: "2px 8px", border: "0.5px solid rgba(255,255,255,0.08)" }}>
                                {row.draw === "Consolation" ? "[Cons] " : ""}{row.stage}
                                <span style={{ marginLeft: 4, color: row.outcome === "W" ? "#6DC878" : "#aaa" }}>{"(" + row.outcome + " +" + row.points + ")"}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p style={{ textAlign: "center", fontSize: 11, color: "#444", marginTop: 20 }}>Open Individual Points Table · Grade A = Grade 3 · Grade B = Grade 4 · Grade C = Grade 5</p>
    </div>
  );
}

// ── Averages ─────────────────────────────────────────────────────────────────

function AveragesPage() {
  var allMatches = useResults().matches;
  var [search, setSearch] = useState("");
  var [minMatches, setMinMatches] = useState(1);

  var allAverages = useMemo(function() {
    var map = computePlayerAverages(allMatches);
    var stats = Object.keys(map).map(function(name) {
      return { player: name, average: map[name].average, best: map[name].best, worst: map[name].worst, matches: map[name].matches };
    });
    stats.sort(function(a, b) { return b.average - a.average; });
    return stats;
  }, [allMatches]);

  var shown = allAverages.filter(function(p) {
    var nameMatch = p.player.toLowerCase().indexOf(search.toLowerCase()) !== -1;
    var matchCount = p.matches >= minMatches;
    return nameMatch && matchCount;
  });

  var maxAvg = shown.length > 0 ? shown[0].average : 1;

  var filterBtns = [
    { label: "All", val: 1 },
    { label: "5+ matches", val: 5 },
    { label: "10+ matches", val: 10 },
    { label: "20+ matches", val: 20 },
  ];

  return (
    <div style={{ padding: "24px 20px", maxWidth: 780, margin: "0 auto", color: "#fff", fontFamily: "sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 40 }}>{"📊"}</div>
        <h1 style={{ margin: "8px 0 4px", fontSize: 40, fontWeight: 800, textTransform: "uppercase", background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Averages</h1>
        <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{allAverages.length + " players · ranked by average score"}</p>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={function(e) { setSearch(e.target.value); }}
          placeholder="Search player..."
          style={{ flex: 1, minWidth: 160, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,165,0,0.2)", borderRadius: 8, color: "#fff", padding: "9px 14px", fontSize: 14, outline: "none" }}
        />
        <div style={{ display: "flex", gap: 6 }}>
          {filterBtns.map(function(b, bi) {
            var active = minMatches === b.val;
            return (
              <button key={bi} onClick={function() { setMinMatches(b.val); }} style={{ background: active ? "rgba(255,176,32,0.15)" : "rgba(255,255,255,0.05)", border: active ? "1px solid rgba(255,176,32,0.4)" : "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: active ? "#FFB020" : "#777", padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>
                {b.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ fontSize: 12, color: "#555", marginBottom: 10 }}>
        {"Showing " + shown.length + " players"}
      </div>

      <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,165,0,0.1)", borderRadius: 14, overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "44px 1fr 70px 70px 70px 54px", gap: 8, padding: "8px 16px", borderBottom: "1px solid rgba(255,255,255,0.08)", fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1 }}>
          <div></div>
          <div>Player</div>
          <div style={{ textAlign: "right" }}>Average</div>
          <div style={{ textAlign: "right" }}>Best</div>
          <div style={{ textAlign: "right" }}>Worst</div>
          <div style={{ textAlign: "right" }}>Matches</div>
        </div>
        {shown.length === 0
          ? <div style={{ textAlign: "center", padding: "60px 0", color: "#555", fontSize: 18 }}>No players found</div>
          : shown.map(function(p, pi) {
              var rank = allAverages.filter(function(a) { return a.matches >= minMatches; }).indexOf(p) + 1;
              var pct = Math.round((p.average / maxAvg) * 100);
              var medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : null;
              return (
                <div key={pi} style={{ display: "grid", gridTemplateColumns: "44px 1fr 70px 70px 70px 54px", gap: 8, alignItems: "center", padding: "11px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.05)" }}>
                  <div style={{ textAlign: "center", fontSize: medal ? 18 : 13, fontWeight: 700, color: "#555" }}>{medal ? medal : rank}</div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: rank <= 3 ? "#FFB020" : "#ddd" }}>{p.player}</div>
                    <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 2, marginTop: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: pct + "%", background: "linear-gradient(90deg,#7BA7FF,#FFB020)", borderRadius: 2 }} />
                    </div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 17, fontWeight: 800, color: rank <= 3 ? "#FFB020" : "#fff" }}>{p.average}</div>
                  <div style={{ textAlign: "right", fontSize: 13, color: "#6DC878" }}>{p.best}</div>
                  <div style={{ textAlign: "right", fontSize: 13, color: "#888" }}>{p.worst}</div>
                  <div style={{ textAlign: "right", fontSize: 13, color: "#666" }}>{p.matches}</div>
                </div>
              );
            })
        }
      </div>
      <p style={{ textAlign: "center", fontSize: 11, color: "#444", marginTop: 20 }}>Average calculated across all recorded matches</p>
    </div>
  );
}

// ── Tournaments ──────────────────────────────────────────────────────────────

var TOURN_MAIN_ORDER = ["Round of 64","Round of 32","Round of 16","Quarter Finals","Semi Finals","Final"];
var TOURN_CONS_ORDER = ["Consolation Round of 32","Consolation Round of 16","Consolation Quarter Finals","Consolation Semi Finals","Consolation Final"];

function TournamentsPage(props) {
  var focusTournamentName = props.focusTournamentName;
  var focusGrade = props.focusGrade;
  var allMatches = useResults().matches;

  var GRADE_COL = { "Grade A": "#FFB020", "Grade B": "#7BA7FF", "Grade C": "#6DC878" };
  var MAIN_ORDER = TOURN_MAIN_ORDER;
  var CONS_ORDER = TOURN_CONS_ORDER;

  var allTournaments = useMemo(function() {
    function isCons(s) { return s && s.trim().startsWith("Consolation"); }
    // Group matches by tournament
    var tMap = {};
    allMatches.forEach(function(m) {
      var tn = m.tournament;
      if (!tMap[tn]) tMap[tn] = { name: tn, date: m.date, matches: [], grades: new Set() };
      if (m.date > tMap[tn].date) tMap[tn].date = m.date;
      tMap[tn].matches.push(m);
      if (m.grade) tMap[tn].grades.add(m.grade);
    });

    return Object.values(tMap).map(function(t) {
      var grades = Array.from(t.grades).sort();
      var gradesToProcess = grades.length > 0 ? grades : [null];
      var is_edc = t.name.indexOf("EDC") !== -1 || t.name.indexOf("02.26") !== -1;

      var grade_draws = gradesToProcess.map(function(grade) {
        var gm = grade ? t.matches.filter(function(m){ return m.grade === grade; }) : t.matches;

        // Round robin groups
        var rr_groups = {};
        var rrMatches = gm.filter(function(m){ return m.stage === "Round Robin"; });
        var groupNames = Array.from(new Set(rrMatches.map(function(m){ return m.group; }).filter(Boolean)));
        groupNames.forEach(function(grpName) {
          var grpMatches = rrMatches.filter(function(m){ return m.group === grpName; });
          var standings = {};
          grpMatches.forEach(function(m) {
            if (!standings[m.player1]) standings[m.player1] = {w:0,l:0,pts:0};
            if (!standings[m.player2]) standings[m.player2] = {w:0,l:0,pts:0};
            var p1won = m.score1 > m.score2;
            standings[m.player1].w += p1won ? 1 : 0;
            standings[m.player1].l += p1won ? 0 : 1;
            standings[m.player1].pts += p1won ? 1 : 0;
            standings[m.player2].w += p1won ? 0 : 1;
            standings[m.player2].l += p1won ? 1 : 0;
            standings[m.player2].pts += p1won ? 0 : 1;
          });
          var table = Object.keys(standings).map(function(p){ return {player:p, w:standings[p].w, l:standings[p].l, pts:standings[p].pts}; });
          table.sort(function(a,b){ return b.pts - a.pts; });
          rr_groups["Group " + grpName] = {
            table: table,
            matches: grpMatches.map(function(m){
              var p1won = m.score1 > m.score2;
              return {p1:m.player1,p2:m.player2,s1:m.score1,s2:m.score2,
                avg1:p1won?m.winner_avg:m.loser_avg, avg2:p1won?m.loser_avg:m.winner_avg};
            })
          };
        });

        // Main draw
        var main = {};
        gm.filter(function(m){ return !isCons(m.stage) && TOURN_MAIN_ORDER.indexOf(m.stage) !== -1; }).forEach(function(m){
          if (!main[m.stage]) main[m.stage] = [];
          var p1won = m.score1 > m.score2;
          main[m.stage].push({p1:m.player1,p2:m.player2,s1:m.score1,s2:m.score2,
            winner:p1won?m.player1:m.player2,
            avg1:p1won?m.winner_avg:m.loser_avg, avg2:p1won?m.loser_avg:m.winner_avg});
        });

        // Consolation draw
        var consolation = {};
        gm.filter(function(m){ return isCons(m.stage) && m.stage.trim() !== "Consolation Other"; }).forEach(function(m){
          var s = m.stage.trim();
          if (!consolation[s]) consolation[s] = [];
          var p1won = m.score1 > m.score2;
          consolation[s].push({p1:m.player1,p2:m.player2,s1:m.score1,s2:m.score2,
            winner:p1won?m.player1:m.player2,
            avg1:p1won?m.winner_avg:m.loser_avg, avg2:p1won?m.loser_avg:m.winner_avg});
        });

        var main_winner = main["Final"] && main["Final"][0] ? main["Final"][0].winner : null;
        var cons_winner = consolation["Consolation Final"] && consolation["Consolation Final"][0] ? consolation["Consolation Final"][0].winner : null;

        return { grade:grade, rr_groups:rr_groups, main:main, consolation:consolation, main_winner:main_winner, cons_winner:cons_winner };
      });

      return { name:t.name, date:t.date, is_edc:is_edc, match_count:t.matches.length, grades:grades, grade_draws:grade_draws };
    }).sort(function(a,b){ return b.date.localeCompare(a.date); });
  }, [allMatches]);

  var [selected, setSelected] = useState(function() {
    if (!focusTournamentName) return null;
    return allTournaments.find(function(t) { return t.name === focusTournamentName; }) || null;
  });
  var [selectedGrade, setSelectedGrade] = useState(function() {
    if (!focusTournamentName) return null;
    var t = allTournaments.find(function(t) { return t.name === focusTournamentName; });
    if (!t) return null;
    return focusGrade && t.grades.indexOf(focusGrade) !== -1 ? focusGrade : (t.grades.length > 0 ? t.grades[0] : null);
  });
  var focusNotFound = !!focusTournamentName && !selected;

  if (selected) {
    var t = selected;
    var draw = selectedGrade ? t.grade_draws.find(function(d){ return d.grade === selectedGrade; }) : t.grade_draws[0];

    // Bracket match component inline
    function MatchBox(props) {
      var m = props.match;
      var p1won = m.s1 > m.s2;
      return (
        <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 10px", minWidth: 220, fontSize: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ color: p1won ? "#FFB020" : "#aaa", fontWeight: p1won ? 700 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.p1}</span>
            <span style={{ fontWeight: 800, color: p1won ? "#FFB020" : "#888", marginLeft: 8, minWidth: 16, textAlign: "right" }}>{m.s1}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: !p1won ? "#FFB020" : "#aaa", fontWeight: !p1won ? 700 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.p2}</span>
            <span style={{ fontWeight: 800, color: !p1won ? "#FFB020" : "#888", marginLeft: 8, minWidth: 16, textAlign: "right" }}>{m.s2}</span>
          </div>
          {(m.avg1 || m.avg2) && (
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, paddingTop: 4, borderTop: "0.5px solid rgba(255,255,255,0.06)", fontSize: 10, color: "#555" }}>
              <span>{m.avg1 ? m.avg1 : "-"}</span>
              <span style={{ color: "#444" }}>avg</span>
              <span>{m.avg2 ? m.avg2 : "-"}</span>
            </div>
          )}
        </div>
      );
    }

    function BracketRound(props) {
      var matches = props.matches;
      var label = props.label;
      var isFinal = label === "Final" || label === "Consolation Final";
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 240 }}>
          <div style={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10, textAlign: "center" }}>{label}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {matches.map(function(m, mi) {
              return (
                <div key={mi} style={{ border: isFinal ? "1px solid rgba(255,176,32,0.3)" : "none", borderRadius: isFinal ? 10 : 0 }}>
                  <MatchBox match={m} />
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    function DrawSection(props) {
      var matches = props.matches;
      var order = props.order;
      var title = props.title;
      var col = props.col || "#FFB020";
      var stages = order.filter(function(s) { return matches[s] && matches[s].length > 0; });
      if (stages.length === 0) return null;
      return (
        <div style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: col, textTransform: "uppercase", letterSpacing: 1, marginBottom: 14, paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>{title}</div>
          <div style={{ overflowX: "auto", paddingBottom: 8 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              {stages.map(function(stage) {
                return <BracketRound key={stage} label={stage} matches={matches[stage]} />;
              })}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px", color: "#fff", fontFamily: "sans-serif" }}>
        <button onClick={function(){ setSelected(null); setSelectedGrade(null); }} style={{ background: "none", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#888", padding: "6px 14px", fontSize: 13, cursor: "pointer", marginBottom: 20 }}>{"← All Tournaments"}</button>

        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 4px", fontSize: 28, fontWeight: 800, color: "#fff" }}>{t.name}</h2>
          <div style={{ fontSize: 13, color: "#666" }}>{new Date(t.date).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" })} · {t.match_count} matches</div>
        </div>

        {t.grades.length > 1 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
            {t.grade_draws.map(function(d, di) {
              var gc = GRADE_COL[d.grade] || "#aaa";
              var active = draw === d;
              return (
                <button key={di} onClick={function(){ setSelectedGrade(d.grade); }} style={{ background: active ? "rgba(255,176,32,0.12)" : "rgba(255,255,255,0.04)", border: "1px solid " + (active ? gc : "rgba(255,255,255,0.1)"), borderRadius: 8, color: active ? gc : "#888", padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: active ? 700 : 400 }}>
                  {d.grade || "Draw"}
                  {d.main_winner && <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.7 }}>{"🏆 " + d.main_winner.split(",")[0]}</span>}
                </button>
              );
            })}
          </div>
        )}

        {draw && (
          <div>
            {draw.main_winner && (
              <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
                <div style={{ background: "rgba(255,176,32,0.08)", border: "1px solid rgba(255,176,32,0.25)", borderRadius: 10, padding: "12px 18px", flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{"🏆 Main Draw Winner"}</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#FFB020" }}>{draw.main_winner}</div>
                </div>
                {draw.cons_winner && (
                  <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px 18px", flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{"🎯 Consolation Winner"}</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#ccc" }}>{draw.cons_winner}</div>
                  </div>
                )}
              </div>
            )}

            {Object.keys(draw.rr_groups).length > 0 && (
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#FFB020", textTransform: "uppercase", letterSpacing: 1, marginBottom: 14, paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>Round Robin Groups</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
                  {Object.entries(draw.rr_groups).map(function(entry, gi) {
                    var grpName = entry[0]; var grpData = entry[1];
                    return (
                      <div key={gi} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
                        <div style={{ background: "rgba(255,176,32,0.08)", padding: "8px 14px", fontSize: 12, fontWeight: 700, color: "#FFB020", letterSpacing: 1 }}>{grpName.toUpperCase()}</div>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                              <th style={{ padding: "6px 14px", textAlign: "left", color: "#555", fontWeight: 400 }}>Player</th>
                              <th style={{ padding: "6px 8px", textAlign: "center", color: "#555", fontWeight: 400 }}>W</th>
                              <th style={{ padding: "6px 8px", textAlign: "center", color: "#555", fontWeight: 400 }}>L</th>
                              <th style={{ padding: "6px 8px", textAlign: "center", color: "#555", fontWeight: 400 }}>Pts</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grpData.table.map(function(row, ri) {
                              return (
                                <tr key={ri} style={{ borderBottom: "0.5px solid rgba(255,255,255,0.05)", background: ri === 0 ? "rgba(255,176,32,0.05)" : "transparent" }}>
                                  <td style={{ padding: "7px 14px", color: ri === 0 ? "#FFB020" : "#ccc", fontWeight: ri === 0 ? 600 : 400 }}>{row.player}</td>
                                  <td style={{ padding: "7px 8px", textAlign: "center", color: "#6DC878" }}>{row.w}</td>
                                  <td style={{ padding: "7px 8px", textAlign: "center", color: "#888" }}>{row.l}</td>
                                  <td style={{ padding: "7px 8px", textAlign: "center", color: "#fff", fontWeight: 700 }}>{row.pts}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <DrawSection matches={draw.main} order={MAIN_ORDER} title={"Main Draw"} col={"#FFB020"} />
            <DrawSection matches={draw.consolation} order={CONS_ORDER} title={"Consolation Draw"} col={"#7BA7FF"} />
          </div>
        )}
      </div>
    );
  }

  // Tournament list view
  return (
    <div style={{ maxWidth: 780, margin: "0 auto", padding: "24px 20px", color: "#fff", fontFamily: "sans-serif" }}>
      <div style={{ textAlign: "center", marginBottom: 28 }}>
        <div style={{ fontSize: 40 }}>{"🗓"}</div>
        <h1 style={{ margin: "8px 0 4px", fontSize: 40, fontWeight: 800, textTransform: "uppercase", background: "linear-gradient(135deg,#FFB020,#FF6B00)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Tournaments</h1>
        <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{allTournaments.length + " events · click to view draw"}</p>
      </div>

      {focusNotFound && (
        <div style={{ background: "rgba(255,176,32,0.08)", border: "1px solid rgba(255,176,32,0.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#FFB020", marginBottom: 16, textAlign: "center" }}>
          {"Couldn't find match-by-match results for \"" + focusTournamentName + "\" — showing all tournaments instead."}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {allTournaments.map(function(t, ti) {
          var mainWinners = t.grade_draws.filter(function(d){ return d.main_winner; });
          var isEDC = t.is_edc;
          return (
            <div key={ti}
              onClick={function(){ setSelected(t); setSelectedGrade(t.grades.length > 0 ? t.grades[0] : null); }}
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,165,0,0.12)", borderRadius: 12, padding: "14px 18px", cursor: "pointer", transition: "border-color 0.15s" }}
              onMouseEnter={function(e){ e.currentTarget.style.borderColor = "rgba(255,165,0,0.4)"; }}
              onMouseLeave={function(e){ e.currentTarget.style.borderColor = "rgba(255,165,0,0.12)"; }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#ddd" }}>{t.name}</span>
                    {isEDC && <span style={{ fontSize: 10, color: "#666", background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "1px 6px" }}>EDC</span>}
                  </div>
                  <div style={{ fontSize: 12, color: "#555", marginBottom: 8 }}>
                    {new Date(t.date).toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" })} · {t.match_count} matches
                  </div>
                  {mainWinners.length > 0 && (
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {mainWinners.map(function(d, di) {
                        var gc = GRADE_COL[d.grade] || "#FFB020";
                        return (
                          <div key={di} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                            {d.grade && <span style={{ fontSize: 10, color: gc, border: "1px solid " + gc + "44", borderRadius: 20, padding: "1px 6px" }}>{d.grade}</span>}
                            <span style={{ fontSize: 12, color: "#ccc" }}>{"🏆 " + d.main_winner}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, color: "#555", marginLeft: 12 }}>{"›"}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  var [route] = useState(getInitialRoute);
  var [activePage, setActivePage] = useState(route.page);
  var [authUser, setAuthUser] = useState(null);
  var [authLoading, setAuthLoading] = useState(true);
  var [rankingsSearch, setRankingsSearch] = useState("");
  var [tournamentsFocus, setTournamentsFocus] = useState(null);

  function goToPlayerRankings(playerName) {
    setRankingsSearch(playerName);
    setActivePage("rankings");
  }

  function goToTournament(tournamentName, grade) {
    setTournamentsFocus({ name: tournamentName, grade: grade || null });
    setActivePage("tournaments");
  }

  useEffect(function() {
    return onAuthStateChanged(auth, function(firebaseUser) {
      if (firebaseUser) {
        getDoc(doc(db, "users", firebaseUser.uid)).then(function(snap) {
          setAuthUser({ uid: firebaseUser.uid, email: firebaseUser.email, playerName: snap.exists() ? snap.data().playerName : null, isAdmin: snap.exists() ? !!snap.data().isAdmin : false });
          setAuthLoading(false);
        });
      } else {
        setAuthUser(null);
        setAuthLoading(false);
      }
    });
  }, []);

  return (
    <ResultsProvider>
    <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at top,#1a0f00 0%,#0d0d0d 60%)", color: "#fff", fontFamily: "sans-serif" }}>
      <NavBar activePage={activePage} onNavigate={setActivePage} authUser={authUser} onAuthNav={function(){ setActivePage(authUser ? "dashboard" : "login"); }} />
      {authLoading ? null :
        activePage === "home" ? <HomePage onNavigate={setActivePage} /> :
        activePage === "login" ? <AuthPage onLogin={function(u){ setAuthUser(u); setActivePage("dashboard"); }} /> :
        activePage === "dashboard" ? (authUser ? <Dashboard user={authUser} onLogout={function(){ setAuthUser(null); setActivePage("home"); }} /> : <AuthPage onLogin={function(u){ setAuthUser(u); setActivePage("dashboard"); }} />) :
        activePage === "matches" ? <MatchList /> :
        activePage === "rankings" ? <RankingsPage initialSearch={rankingsSearch} onNavigateToTournament={goToTournament} /> :
        activePage === "averages" ? <AveragesPage /> :
        activePage === "tournaments" ? <TournamentsPage focusTournamentName={tournamentsFocus ? tournamentsFocus.name : null} focusGrade={tournamentsFocus ? tournamentsFocus.grade : null} /> :
        activePage === "enter" ? <TournamentEntryPage user={authUser} focusEntryId={route.entryId} paymentReturn={route.payment} onNavigateToPlayer={goToPlayerRankings} /> :
        activePage === "admin" ? (authUser && authUser.isAdmin ? <AdminPage /> : <div style={{ textAlign: "center", padding: "60px 20px", color: "#888", fontFamily: "sans-serif" }}>You don't have access to this page.</div>) :
        <ScorePage user={authUser} />
      }
    </div>
    </ResultsProvider>
  );
}